import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { api } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { User, Lock, Save, Loader2, ShieldCheck, ShieldOff, Smartphone, Trash2, Eye, EyeOff, Mail, Phone, Building2, BadgeCheck } from 'lucide-react';
import { validatePassword, trimText } from '@/lib/passwordValidation';

const NAME_PATTERN = /^[A-Za-z\s.\-']+$/;
const DEPT_PATTERN = /^[A-Za-z\s&()\-,.]+$/;
const DESIG_PATTERN = /^[A-Za-z\s\-/&.]+$/;

function validateName(name) {
  if (!name) return 'This field cannot be empty or contain only spaces.';
  if (name.length < 2) return 'Name must be at least 2 characters';
  if (name.length > 100) return 'Name must be at most 100 characters';
  if (!NAME_PATTERN.test(name)) return 'Name must contain only letters, spaces, dots, hyphens, or apostrophes';
  return null;
}

function validateOptionalField(value, maxLen, pattern, patternMsg) {
  if (!value) return null;
  if (value.length > maxLen) return `Must be at most ${maxLen} characters`;
  if (!pattern.test(value)) return patternMsg;
  return null;
}

function validateProfile(profile) {
  const errors = {};
  const nameErr = validateName(trimText(profile.full_name || ''));
  if (nameErr) errors.full_name = nameErr;

  const deptErr = validateOptionalField(trimText(profile.department || ''), 100, DEPT_PATTERN, 'Only letters, spaces, and symbols & ( ) - , . are allowed');
  if (deptErr) errors.department = deptErr;

  const desigErr = validateOptionalField(trimText(profile.designation || ''), 100, DESIG_PATTERN, 'Only letters, spaces, and symbols - / & . are allowed');
  if (desigErr) errors.designation = desigErr;

  const phone = trimText(profile.phone || '');
  if (phone && phone.length !== 10) errors.phone = 'Phone must be exactly 10 digits';

  return errors;
}

/** Trim the editable profile text fields. Extracted from handleProfileSave to
 *  keep its cognitive complexity within limits; the trimmed shape is unchanged. */
function buildCleanProfile(profile) {
  return {
    ...profile,
    full_name: trimText(profile.full_name || ''),
    department: trimText(profile.department || ''),
    designation: trimText(profile.designation || ''),
    phone: trimText(profile.phone || ''),
  };
}

/** Small reusable row for the account summary rail. */
function SummaryRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate">{value || '—'}</p>
      </div>
    </div>
  );
}

SummaryRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

/** Derive the avatar display name, initials and role label from the current
 *  profile + user. Pure; extracted from ProfilePage to keep it shallow. */
function deriveProfileDisplay(profile, user) {
  const displayName = trimText(profile.full_name || '') || user?.full_name || 'Your Name';
  const initials = (trimText(profile.full_name || user?.full_name || '') || user?.email || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase())
    .join('') || 'U';
  const roleLabel = user?.role_display || user?.role || (user?.is_system_admin ? 'System Admin' : 'User');
  return { displayName, initials, roleLabel };
}

/** Left summary rail: avatar, name, role/2FA badges and contact rows. */
function ProfileSummaryRail({ displayName, initials, roleLabel, is2FA, user, profile }) {
  return (
    <Card className="lg:sticky lg:top-6 shadow-sm">
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center">
          <Avatar className="h-20 w-20 ring-4 ring-muted">
            <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <h2 className="mt-4 text-lg font-semibold tracking-tight break-words">{displayName}</h2>
          {user?.designation && (
            <p className="text-sm text-muted-foreground">{user.designation}</p>
          )}
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <Badge variant="secondary" className="capitalize">{roleLabel}</Badge>
            {is2FA ? (
              <Badge variant="outline" className="gap-1 border-green-200 text-green-700 dark:border-green-900 dark:text-green-400">
                <ShieldCheck className="h-3 w-3" /> 2FA On
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <ShieldOff className="h-3 w-3" /> 2FA Off
              </Badge>
            )}
          </div>
        </div>

        <Separator className="my-5" />

        <div className="space-y-4">
          <SummaryRow icon={Mail} label="Email" value={user?.email} />
          <SummaryRow icon={Phone} label="Phone" value={profile.phone} />
          <SummaryRow icon={Building2} label="Department" value={profile.department} />
          <SummaryRow icon={BadgeCheck} label="Designation" value={profile.designation} />
        </div>
      </CardContent>
    </Card>
  );
}

ProfileSummaryRail.propTypes = {
  displayName: PropTypes.string,
  initials: PropTypes.string,
  roleLabel: PropTypes.node,
  is2FA: PropTypes.bool,
  user: PropTypes.object,
  profile: PropTypes.object.isRequired,
};

/** Consistent section-card title with an accent icon chip. */
function SectionTitle({ icon: Icon, chip, children }) {
  return (
    <CardTitle className="flex items-center gap-2.5 text-base">
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      {children}
    </CardTitle>
  );
}

SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  chip: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/** Personal Information card: name/email/phone/department/designation form. */
function PersonalInfoCard({ profile, setProfile, profileErrors, setProfileErrors, trimProfileFieldOnBlur, handleProfileSave, savingProfile, user }) {
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <SectionTitle icon={User} chip="bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
          Personal Information
        </SectionTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleProfileSave} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={profile.full_name} onChange={e => { const val = e.target.value.replace(/[^A-Za-z\s.\-']/g, ''); setProfile(p => ({ ...p, full_name: val })); setProfileErrors(prev => ({ ...prev, full_name: undefined })); }} onBlur={trimProfileFieldOnBlur('full_name')} required maxLength={100} placeholder="Enter your full name" className={profileErrors.full_name ? 'border-red-500' : ''} data-testid="profile-name" />
              {profileErrors.full_name && <p className="text-xs text-red-500">{profileErrors.full_name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="opacity-60" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={profile.phone}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setProfile(p => ({ ...p, phone: val }));
                }}
                placeholder="10-digit mobile number"
                data-testid="profile-phone"
              />
              {profile.phone && profile.phone.length > 0 && profile.phone.length < 10 && (
                <p className="text-xs text-red-500">Phone must be exactly 10 digits</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Input value={profile.department} onChange={e => { const val = e.target.value.replace(/[^A-Za-z\s&()\-,.]/g, ''); setProfile(p => ({ ...p, department: val })); setProfileErrors(prev => ({ ...prev, department: undefined })); }} onBlur={trimProfileFieldOnBlur('department')} maxLength={100} placeholder="e.g. Credit Operations" className={profileErrors.department ? 'border-red-500' : ''} data-testid="profile-dept" />
              {profileErrors.department && <p className="text-xs text-red-500">{profileErrors.department}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Designation</Label>
              <Input value={profile.designation} onChange={e => { const val = e.target.value.replace(/[^A-Za-z\s\-/&.]/g, ''); setProfile(p => ({ ...p, designation: val })); setProfileErrors(prev => ({ ...prev, designation: undefined })); }} onBlur={trimProfileFieldOnBlur('designation')} maxLength={100} placeholder="e.g. Loan Officer" className={profileErrors.designation ? 'border-red-500' : ''} data-testid="profile-desig" />
              {profileErrors.designation && <p className="text-xs text-red-500">{profileErrors.designation}</p>}
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

PersonalInfoCard.propTypes = {
  profile: PropTypes.object.isRequired,
  setProfile: PropTypes.func.isRequired,
  profileErrors: PropTypes.object.isRequired,
  setProfileErrors: PropTypes.func.isRequired,
  trimProfileFieldOnBlur: PropTypes.func.isRequired,
  handleProfileSave: PropTypes.func.isRequired,
  savingProfile: PropTypes.bool,
  user: PropTypes.object,
};

/** Change Password card: current/new/confirm password form. */
function ChangePasswordCard({ passwords, setPasswords, showCurrentPw, setShowCurrentPw, handlePasswordChange, savingPassword }) {
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <SectionTitle icon={Lock} chip="bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
          Change Password
        </SectionTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handlePasswordChange} className="space-y-5">

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:max-w-sm">
              <Label>Current Password</Label>
              <div className="relative">
                <Input type={showCurrentPw ? 'text' : 'password'} value={passwords.current_password} onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))} required placeholder="Enter current password" className="pr-10" data-testid="current-password" />
                <button type="button" onClick={() => setShowCurrentPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} aria-label={showCurrentPw ? 'Hide password' : 'Show password'}>
                  {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input type="password" value={passwords.new_password} onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} required minLength={8} placeholder="Enter new password" data-testid="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm New Password</Label>
              <Input type="password" value={passwords.confirm_password} onChange={e => setPasswords(p => ({ ...p, confirm_password: e.target.value }))} required minLength={8} placeholder="Re-enter new password" data-testid="confirm-password" />
            </div>
          </div>
         
          <div className="flex justify-end ">
            <Button type="submit" disabled={savingPassword} data-testid="change-password-btn">
              {savingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
              Change Password
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

ChangePasswordCard.propTypes = {
  passwords: PropTypes.object.isRequired,
  setPasswords: PropTypes.func.isRequired,
  showCurrentPw: PropTypes.bool,
  setShowCurrentPw: PropTypes.func.isRequired,
  handlePasswordChange: PropTypes.func.isRequired,
  savingPassword: PropTypes.bool,
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    department: user?.department || '',
    designation: user?.designation || '',
  });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [showCurrentPw, setShowCurrentPw] = useState(false);

  // Lifted so the summary rail and the 2FA section share one source of truth.
  const [twoFAStatus, setTwoFAStatus] = useState(null);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    const cleanProfile = buildCleanProfile(profile);
    const errors = validateProfile(cleanProfile);
    setProfileErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors before saving');
      return;
    }
    setSavingProfile(true);
    try {
      setProfile(cleanProfile);
      const updated = await api.put('/api/auth/profile', cleanProfile);
      updateUser(updated);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.message); }
    finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    // Current password: trim ends (like login). New password: never trimmed — spaces rejected by policy.
    const currentPassword = trimText(passwords.current_password);
    const newPassword = passwords.new_password;
    const confirmPassword = passwords.confirm_password;
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    setSavingPassword(true);
    try {
      await api.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.message); }
    finally { setSavingPassword(false); }
  };

  const trimProfileFieldOnBlur = (field) => (e) => {
    const trimmed = trimText(e.target.value);
    if (trimmed !== e.target.value) {
      setProfile(p => ({ ...p, [field]: trimmed }));
    }
  };

  const { displayName, initials, roleLabel } = deriveProfileDisplay(profile, user);
  const is2FA = Boolean(twoFAStatus?.is_2fa_enabled);

  return (
    <div className="space-y-6 w-full max-w-[1200px] mx-auto" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My Profile</h1>
        <p className="text-muted-foreground text-sm">Manage your account details and security</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 items-start">
        {/* ── Left summary rail ───────────────────────────────── */}
        <ProfileSummaryRail
          displayName={displayName}
          initials={initials}
          roleLabel={roleLabel}
          is2FA={is2FA}
          user={user}
          profile={profile}
        />

        {/* ── Right content column ────────────────────────────── */}
        <div className="space-y-6 min-w-0">
          <PersonalInfoCard
            profile={profile}
            setProfile={setProfile}
            profileErrors={profileErrors}
            setProfileErrors={setProfileErrors}
            trimProfileFieldOnBlur={trimProfileFieldOnBlur}
            handleProfileSave={handleProfileSave}
            savingProfile={savingProfile}
            user={user}
          />

          <ChangePasswordCard
            passwords={passwords}
            setPasswords={setPasswords}
            showCurrentPw={showCurrentPw}
            setShowCurrentPw={setShowCurrentPw}
            handlePasswordChange={handlePasswordChange}
            savingPassword={savingPassword}
          />

          {/* 2FA Security Section */}
          <TwoFASection onStatusChange={setTwoFAStatus} />
          <TrustedDevicesSection />

          {/* Registration Settings — system admin only, hidden in production */}
          {user?.is_system_admin && process.env.REACT_APP_HIDE_DEV_SETTINGS !== 'true' && <RegistrationSettingsSection />}
        </div>
      </div>
    </div>
  );
}

function TwoFAOTPStep({ otpStep, otp, onOtpChange, onConfirm, onCancel, submitting }) {
  return (
    <div className="space-y-4" data-testid="twofa-otp-step">
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code sent to your email to {otpStep === 'enable' ? 'enable' : 'disable'} 2FA.
      </p>
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={onOtpChange}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>
      <div className="flex gap-2 justify-center">
        <Button
          id="twofa-confirm-btn"
          onClick={onConfirm}
          disabled={otp.length !== 6 || submitting}
          data-testid="twofa-confirm-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {submitting ? 'Verifying...' : 'Confirm'}
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="twofa-cancel-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
}

TwoFAOTPStep.propTypes = {
  otpStep: PropTypes.string,
  otp: PropTypes.string.isRequired,
  onOtpChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

function TwoFAToggleSection({ is2FA, status, handleSendOtp, sending }) {
  const enabledDate = status?.enabled_at ? new Date(status.enabled_at).toLocaleDateString() : 'N/A';
  const statusText = is2FA ? `Enabled since ${enabledDate}` : 'Not enabled';
  return (
    <div className="flex items-center justify-between gap-4" data-testid="twofa-toggle-section">
      <div className="text-sm text-muted-foreground">
        {statusText}
      </div>
      {is2FA ? (
        <Button variant="outline" onClick={() => handleSendOtp('disable')} disabled={sending} data-testid="twofa-disable-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldOff className="h-4 w-4 mr-2" />}
          Disable 2FA
        </Button>
      ) : (
        <Button onClick={() => handleSendOtp('enable')} disabled={sending} data-testid="twofa-enable-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          Enable 2FA
        </Button>
      )}
    </div>
  );
}

TwoFAToggleSection.propTypes = {
  is2FA: PropTypes.bool.isRequired,
  status: PropTypes.shape({ enabled_at: PropTypes.string }),
  handleSendOtp: PropTypes.func.isRequired,
  sending: PropTypes.bool,
};

function TwoFASection({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpStep, setOtpStep] = useState(null); // 'enable' | 'disable' | null
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const data = await api.get('/api/auth/2fa/status');
      setStatus(data);
      onStatusChange?.(data);
    } catch {
      const fallback = { is_2fa_enabled: false };
      setStatus(fallback);
      onStatusChange?.(fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (action) => {
    setSending(true);
    try {
      const endpoint = action === 'enable' ? '/api/auth/2fa/send-enable-otp' : '/api/auth/2fa/send-disable-otp';
      await api.post(endpoint);
      setOtpStep(action);
      setOtp('');
      toast.info('Verification code sent to your email');
    } catch (e) {
      toast.error(e.message || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    if (otp.length !== 6) return;
    setSubmitting(true);
    try {
      const endpoint = otpStep === 'enable' ? '/api/auth/2fa/confirm-enable' : '/api/auth/2fa/confirm-disable';
      const data = await api.post(endpoint, { otp });
      toast.success(data.message);
      setOtpStep(null);
      setOtp('');
      loadStatus();
    } catch (e) {
      toast.error(e.message || 'Verification failed');
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const onOtpChange = (value) => {
    setOtp(value);
    if (value.length === 6) {
      setTimeout(() => document.getElementById('twofa-confirm-btn')?.click(), 200);
    }
  };

  if (loading) return null;

  const is2FA = status?.is_2fa_enabled;

  return (
    <Card data-testid="twofa-section" className="shadow-sm">
      <CardHeader>
        <SectionTitle
          icon={is2FA ? ShieldCheck : ShieldOff}
          chip={is2FA ? 'bg-green-50 text-green-600 dark:bg-green-950/60 dark:text-green-400' : 'bg-muted text-muted-foreground'}
        >
          Two-Factor Authentication
          {is2FA && <Badge variant="default" className="ml-1 bg-green-600 hover:bg-green-600" data-testid="twofa-badge-enabled">Enabled</Badge>}
        </SectionTitle>
        <CardDescription>
          {is2FA
            ? 'Your account is protected with email-based two-factor authentication.'
            : 'Add an extra layer of security by requiring a verification code sent to your email on each login.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {otpStep ? (
          <TwoFAOTPStep
            otpStep={otpStep}
            otp={otp}
            onOtpChange={onOtpChange}
            onConfirm={handleConfirm}
            onCancel={() => { setOtpStep(null); setOtp(''); }}
            submitting={submitting}
          />
        ) : (
          <TwoFAToggleSection is2FA={is2FA} status={status} handleSendOtp={handleSendOtp} sending={sending} />
        )}
      </CardContent>
    </Card>
  );
}

TwoFASection.propTypes = {
  onStatusChange: PropTypes.func,
};

function TrustedDevicesSection() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDevices(); }, []);

  const loadDevices = async () => {
    try {
      const data = await api.get('/api/auth/2fa/trusted-devices');
      setDevices(data);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (deviceId) => {
    try {
      await api.delete(`/api/auth/2fa/trusted-devices/${deviceId}`);
      toast.success('Device removed');
      loadDevices();
    } catch (e) {
      toast.error(e.message || 'Failed to remove device');
    }
  };

  const handleRevokeAll = async () => {
    try {
      await api.delete('/api/auth/2fa/trusted-devices');
      toast.success('All devices removed');
      loadDevices();
    } catch (e) {
      toast.error(e.message || 'Failed to remove devices');
    }
  };

  if (loading) return null;
  if (devices.length === 0) return null;

  return (
    <Card data-testid="trusted-devices-section" className="shadow-sm">
      <CardHeader>
        <SectionTitle icon={Smartphone} chip="bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400">
          Trusted Devices
          <Badge variant="secondary" className="ml-1">{devices.length}</Badge>
        </SectionTitle>
        <CardDescription>
          Devices that can skip 2FA verification for 30 days
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {devices.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card transition-colors hover:bg-muted/40" data-testid={`trusted-device-${d.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{d.device_label}</p>
                <p className="text-xs text-muted-foreground">
                  Added {d.created_at ? new Date(d.created_at).toLocaleDateString() : 'N/A'}
                  {d.last_used_at && ` · Last used ${new Date(d.last_used_at).toLocaleDateString()}`}
                  {!d.is_active && <span className="text-red-500 ml-1">(Expired)</span>}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => handleRevoke(d.id)} data-testid={`revoke-device-${d.id}`}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
        {devices.length > 1 && (
          <Button variant="outline" size="sm" className="w-full" onClick={handleRevokeAll} data-testid="revoke-all-devices">
            Remove all trusted devices
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


function RegistrationSettingsSection() {
  const [allowPersonalEmails, setAllowPersonalEmails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/system/registration-settings')
      .then(data => setAllowPersonalEmails(data.allow_personal_emails))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const data = await api.put('/api/system/registration-settings', { allow_personal_emails: checked });
      setAllowPersonalEmails(data.allow_personal_emails);
      toast.success(checked ? 'Personal email registration enabled' : 'Personal email registration disabled');
    } catch (e) {
      toast.error(e.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="registration-settings-section" className="shadow-sm">
      <CardHeader>
        <SectionTitle icon={Mail} chip="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          Registration Settings
        </SectionTitle>
        <CardDescription>
          Control which email domains are allowed during company registration
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Allow Personal Email Registration</p>
            <p className="text-xs text-muted-foreground">
              When enabled, users can register with personal emails (Gmail, Yahoo, etc.). Disable for production.
            </p>
          </div>
          <Switch
            checked={allowPersonalEmails}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="allow-personal-emails-switch"
          />
        </div>
        {allowPersonalEmails && (
          <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700 font-medium">⚠ Personal email domains (Gmail, Yahoo, Outlook, etc.) are currently allowed for registration.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
